import { useMemo } from 'react';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  EntityType,
  SearchResult,
  useTypeaheadSearchQuery,
} from '@/services/api/searchService';

export const useDuplicateDetection = (matterName: string, delay = 500) => {
  // Debounce the matter name input
  const debouncedName = useDebouncedValue(matterName.trim(), delay);

  // Only search if we have a meaningful query:
  // - At least 5 characters long
  // - Contains a space (to ensure it's likely a first/last name)
  const shouldSearch = debouncedName.length >= 5 && debouncedName.includes(' ');

  const { data, isLoading } = useTypeaheadSearchQuery(debouncedName, {
    skip: !shouldSearch,
  });

  // Filter and validate results using consecutive word matching
  const duplicates: SearchResult[] = useMemo(() => {
    if (!shouldSearch || !data?.results) return [];

    // Filter to only matters and intakes
    const candidates = data.results.filter((result: SearchResult) => {
      const entityType: EntityType = result.entity_type;
      return entityType === 'matter' || entityType === 'intake';
    });

    // Word-level validation to filter false positives
    // Strategy: Look for consecutive name words in the target that match the query
    const queryWords = debouncedName.toLowerCase().trim().split(/\s+/);

    const validatedResults = candidates.filter((item) => {
      const targetWords = item.label.toLowerCase().trim().split(/\s+/);

      // Find the longest consecutive sequence of query words that match in target
      // This handles cases like "dog bite - kevin jackson" where "kevin jackson" should match
      let maxConsecutiveMatches = 0;

      for (let i = 0; i < queryWords.length; i += 1) {
        let consecutiveMatches = 0;
        let targetIndex = 0;

        for (let j = i; j < queryWords.length; j += 1) {
          const queryWord = queryWords[j];
          let foundMatch = false;

          // Look for this query word in remaining target words
          for (let k = targetIndex; k < targetWords.length; k += 1) {
            const targetWord = targetWords[k];

            // Check if this is a valid match
            const isMatch =
              targetWord.startsWith(queryWord) ||
              queryWord.startsWith(targetWord) ||
              levenshteinDistance(queryWord, targetWord) <=
                Math.max(1, Math.floor(queryWord.length * 0.4));

            if (isMatch) {
              consecutiveMatches += 1;
              targetIndex = k + 1;
              foundMatch = true;
              break;
            }
          }

          if (!foundMatch) break;
        }

        maxConsecutiveMatches = Math.max(
          maxConsecutiveMatches,
          consecutiveMatches,
        );
      }

      // Require at least 2 consecutive words to match (first + last name)
      // This filters out "kevin jaques" matching "Kevin Jackson" (only 1 word matches)
      // But allows "dog bite - kevin jackson" to match "Kevin Jackson" (2 consecutive words match)
      return maxConsecutiveMatches >= 2;
    });

    // Return the validated results
    return validatedResults;
  }, [shouldSearch, data?.results, debouncedName]);

  return {
    duplicates,
    isLoading: shouldSearch && isLoading,
    hasChecked: shouldSearch && !isLoading,
  };
};

/**
 * Simple Levenshtein distance calculation for word-level comparison
 */
function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = Array.from({ length: b.length + 1 }, () =>
    Array.from({ length: a.length + 1 }, () => 0),
  );

  for (let i = 0; i <= a.length; i += 1) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= b.length; j += 1) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= b.length; j += 1) {
    for (let i = 1; i <= a.length; i += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j - 1][i] + 1, // deletion
        matrix[j][i - 1] + 1, // insertion
        matrix[j - 1][i - 1] + cost, // substitution
      );
    }
  }

  return matrix[b.length][a.length];
}
